import { FormContent, PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import CaseWorkspace, { CaseFacts } from "@/components/case-workspace";
import {
  locationCustomerPath,
  locationVehiclePath,
  locationVehiclesPath,
  newLocationVehicleOwnershipPath,
} from "@/routes";
import type { VehicleProfile, VehicleOwner } from "./types";
import { CatalogueCredit, type VehicleCatalogue } from "./catalogue-fields";
import VehicleFields, {
  vehicleTargets,
} from "@/components/forms/vehicle-fields";
import VehiclePhotos from "@/components/forms/vehicle-photos";
import { FormErrors } from "@/components/forms/form-errors";

export default function VehicleForm({
  vehicle,
  owner,
  catalogue,
}: {
  vehicle: VehicleProfile | null;
  owner: VehicleOwner;
  catalogue: VehicleCatalogue;
}) {
  const { auth, errors } = usePage<{ errors: Record<string, string> }>().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const title = t(vehicle ? "vehicles.edit" : "vehicles.add");
  return (
    <AppLayout
      breadcrumbs={[
        { title: owner.name, href: locationCustomerPath(locationId, owner.id) },
      ]}
    >
      <Head title={title} />
      <PageCanvas>
        <CaseWorkspace
          context={
            <CaseFacts
              facts={[
                {
                  label: t("vehicles.owner"),
                  value: (
                    <Link
                      className="underline"
                      href={locationCustomerPath(locationId, owner.id)}
                    >
                      {owner.name}
                    </Link>
                  ),
                },
                {
                  label: t("resource_workspace.location"),
                  value: auth.location.name,
                },
                ...(vehicle
                  ? [
                      {
                        label: t("vehicles.details"),
                        value: `${vehicle.make} ${vehicle.model}`,
                      },
                    ]
                  : []),
              ]}
            />
          }
        >
          <FormContent>
            <h1 className="text-page font-display">{title}</h1>

            <FormErrors
              errors={errors || {}}
              targets={{ ...vehicleTargets, files: "vehicle-files" }}
            />
            <Form
              key={vehicle?.revision || "new"}
              action={
                vehicle
                  ? locationVehiclePath(locationId, vehicle.id)
                  : locationVehiclesPath(locationId)
              }
              method={vehicle ? "patch" : "post"}
              encType="multipart/form-data"
            >
              {({ processing }) => (
                <div className="space-y-4">
                  <input type="hidden" name="customer_id" value={owner.id} />
                  {vehicle && (
                    <input
                      type="hidden"
                      name="revision"
                      value={vehicle.revision}
                    />
                  )}
                  <VehicleFields
                    values={vehicle}
                    catalogue={catalogue}
                    errors={errors}
                  />
                  <VehiclePhotos
                    existingPhotos={vehicle?.photos}
                    removeName="remove_photo_ids[]"
                    error={errors?.files}
                    help={t("resource_workspace.photos_reload")}
                  />
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t(vehicle ? "vehicles.save" : "vehicles.add")}
                    </Button>
                    <Button
                      variant="outline"
                      render={
                        <Link
                          href={
                            vehicle
                              ? locationVehiclePath(locationId, vehicle.id)
                              : locationCustomerPath(locationId, owner.id)
                          }
                        />
                      }
                    >
                      {t("customers.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
            <CatalogueCredit />
            {vehicle && (
              <section
                className="space-y-3 border-t pt-6"
                aria-labelledby="vehicle-management-title"
              >
                <h2
                  id="vehicle-management-title"
                  className="text-section font-semibold"
                >
                  {t("record_management.vehicle_management")}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("vehicles.transfer_help")}
                </p>
                <Button
                  variant="outline"
                  render={
                    <Link
                      href={newLocationVehicleOwnershipPath(
                        locationId,
                        vehicle.id,
                      )}
                    />
                  }
                >
                  {t("vehicles.transfer")}
                </Button>
              </section>
            )}
          </FormContent>
        </CaseWorkspace>
      </PageCanvas>
    </AppLayout>
  );
}
